import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Sidebar from './Sidebar';
import TopBar from './TopBar';
import { useAuth } from '../../context/AuthContext';
import { useShift } from '../../hooks/useShift';
import ShiftModals from './shifts/ShiftModals';
import InactiveLock from '../common/InactiveLock';

interface DashboardLayoutProps {
  children: React.ReactNode;
  title?: string;
}

export default function DashboardLayout({ children, title }: DashboardLayoutProps) {
  const [sidebarOpen, setSidebarOpen] = useState(false); // Cerrado por defecto en móvil
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, user } = useAuth();
  
  // Shift Logic
  const { shift, loading, handleOpenShift, handleCloseShift } = useShift();
  const [showOpenModal, setShowOpenModal] = useState(false);
  const [showCloseModal, setShowCloseModal] = useState(false);

  // Abrir sidebar en desktop por defecto
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) { // lg breakpoint
        setSidebarOpen(true);
      } else {
        setSidebarOpen(false);
      }
    };

    handleResize(); // Check initial size
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const isCompanyInactive = user?.company?.status === 'INACTIVA';
  const isSuperAdmin = user?.rol === 'SUPERADMIN';

  // Si la empresa está inactiva, mostrar bloqueo a pantalla completa (sin Sidebar ni TopBar)
  if (isCompanyInactive && !isSuperAdmin) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="absolute top-6 right-6">
           <button 
            onClick={handleLogout}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 text-slate-600 rounded-xl hover:bg-slate-50 transition-all font-medium text-sm shadow-sm"
           >
             <i className="ri-logout-box-line"></i>
             Cerrar Sesión
           </button>
        </div>
        <div className="w-full max-w-2xl">
          <InactiveLock />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Sidebar 
        isOpen={sidebarOpen} 
        currentPath={location.pathname}
        onClose={() => setSidebarOpen(false)}
      />
      
      <div className="lg:ml-64 transition-all duration-300">
        <TopBar 
          onToggleSidebar={() => setSidebarOpen(!sidebarOpen)}
          onLogout={handleLogout}
          leftContent={
            (!loading && !isSuperAdmin && user?.rol === 'OPERADOR') && (
              <div className="flex items-center gap-2 sm:gap-3 bg-slate-50 rounded-full pl-1 pr-2 sm:pr-3 py-1 border border-slate-100">
                  <div className={`w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center ${shift ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'}`}>
                      <i className={`text-sm sm:text-base ${shift ? 'ri-store-2-fill' : 'ri-store-2-line'}`}></i>
                  </div>
                  <div className="hidden sm:flex flex-col">
                      <span className="text-[10px] uppercase font-bold text-slate-400 leading-tight">Caja</span>
                      <span className={`text-xs font-bold leading-tight ${shift ? 'text-green-600' : 'text-red-500'}`}>
                          {shift ? 'ABIERTA' : 'CERRADA'}
                      </span>
                  </div>
                  {shift ? (
                      <button onClick={() => setShowCloseModal(true)} className="ml-1 sm:ml-2 text-[10px] sm:text-xs bg-white border border-red-200 text-red-600 px-2 py-1 rounded-md hover:bg-red-50 font-medium transition">
                          Cerrar
                      </button>
                  ) : (
                      <button onClick={() => setShowOpenModal(true)} className="ml-1 sm:ml-2 text-[10px] sm:text-xs bg-teal-600 text-white px-2 sm:px-3 py-1 rounded-md hover:bg-teal-700 font-medium shadow-sm shadow-teal-200 animate-pulse transition">
                          <span className="hidden sm:inline">Abrir Caja</span>
                          <span className="sm:hidden">Abrir</span>
                      </button>
                  )}
              </div>
            )
          }
        />
        
        <main className="p-4 sm:p-6">
          {title && (
            <div className="mb-4 sm:mb-6">
              <h1 className="text-xl sm:text-2xl font-bold text-slate-800">{title}</h1>
            </div>
          )}
          {children}
        </main>
      </div>

      <ShiftModals 
        isOpenModalOpen={showOpenModal}
        isCloseModalOpen={showCloseModal}
        onClose={() => { setShowOpenModal(false); setShowCloseModal(false); }}
        onOpenShift={handleOpenShift}
        onCloseShift={handleCloseShift}
        currentShift={shift}
      />
    </div>
  );
}
